import { Component } from '@theme/component';

/**
 * Size guide: two tabs (body / product measurements), a multi-select size row
 * that filters the table, and a cm/inch toggle that reformats the numbers.
 *
 * Measurements are authored once in centimetres and converted here, so the two
 * unit systems can never drift apart. Each panel keeps its own size selection
 * and its own unit, matching the reference.
 *
 * When the element carries data-sheet-url, the measurements are fetched from
 * that Google Sheet, filtered to the product's SKUs and sizes, and written into
 * the empty panel before the usual rendering runs. data-has-chart is set only
 * once rows are found, which is what reveals the opener.
 *
 * Expected markup (rendered by blocks/size-guide.liquid):
 *   [data-panel]        one per tab, holding everything below
 *   [data-tab]          tab buttons, value matches a panel
 *   [data-sizes]        container the size toggles are written into
 *   [data-head]         header row the size columns are written into
 *   [data-body]         table body the measurement rows are written into
 *   [data-size]         size toggles inside a panel
 *   [data-col]          size columns, value matches a size
 *   [data-cm]           cells holding a centimetre value or range ("76-79")
 *   [data-unit]         unit toggles inside a panel
 */

const CM_PER_INCH = 2.54;

/** How long a downloaded sheet is reused across page views. */
const SHEET_TTL_MS = 10 * 60 * 1000;

/** Brazilian size letters, as used by the brand, mapped to the ones on the sheet. */
const SIZE_ALIASES = { PP: 'XS', P: 'S', G: 'L', GG: 'XL', XG: 'XXL', XGG: 'XXL' };

/** Sheet columns that are not measurements. */
const SKU_COLUMN = /^sku$/i;
const SIZE_COLUMN = /^(talla|size|tamaño)$/i;
const IGNORED_COLUMNS = /^(nombre|producto|nombre producto|unidad|unit|site|sitio)$/i;

/** @type {Map<string, Promise<string[][]>>} */
const sheetRequests = new Map();

/** Garment measurements are conventionally quoted to the nearest eighth. */
const EIGHTHS = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];

/** A single value or a range, e.g. "78" or "76-79". */
const NUMERIC = /^\d+(?:[.,]\d+)?(?:\s*-\s*\d+(?:[.,]\d+)?)?$/;

class SizeGuideComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.sheetUrl) {
      this.#loadSheet();
    } else {
      this.#renderAll();
    }
  }

  async #loadSheet() {
    const panel = this.querySelector('[data-panel]');
    if (!panel) return;

    let table;
    try {
      table = await fetchSheet(this.dataset.sheetUrl ?? '');
    } catch (error) {
      console.error('Size guide: the sheet could not be loaded.', error);
      return;
    }

    const guide = findProductGuide(table, {
      handle: this.dataset.handle ?? '',
      skus: splitList(this.dataset.skus),
      sizes: splitList(this.dataset.sizes).map(normaliseSize),
    });
    if (!guide) return;

    fillPanel(panel, guide);
    this.dataset.hasChart = '';
    this.#renderAll();
  }

  /**
   * Switches the visible tab.
   * @param {Event} event
   */
  selectTab(event) {
    const name = this.#targetData(event, 'tab');
    if (!name) return;

    for (const tab of this.querySelectorAll('[data-tab]')) {
      tab.setAttribute('aria-selected', String(tab.dataset.tab === name));
    }

    for (const panel of this.#panels) {
      panel.hidden = panel.dataset.panel !== name;
    }
  }

  /**
   * Adds or removes a size from the comparison. Several may be active at once.
   * @param {Event} event
   */
  toggleSize(event) {
    const button = this.#closest(event, '[data-size]');
    if (!button) return;

    const pressed = button.getAttribute('aria-pressed') === 'true';
    button.setAttribute('aria-pressed', String(!pressed));
    this.#render(button.closest('[data-panel]'));
  }

  /**
   * Switches a panel between centimetres and inches.
   * @param {Event} event
   */
  selectUnit(event) {
    const button = this.#closest(event, '[data-unit]');
    if (!button) return;

    const panel = button.closest('[data-panel]');
    if (!panel) return;

    panel.dataset.activeUnit = button.dataset.unit ?? 'cm';
    this.#render(panel);
  }

  get #panels() {
    return this.querySelectorAll('[data-panel]');
  }

  #renderAll() {
    for (const panel of this.#panels) this.#render(panel);
  }

  /**
   * Reflects the panel's selected sizes and unit onto its table.
   * @param {Element | null} panel
   */
  #render(panel) {
    if (!panel) return;

    const selected = new Set(
      Array.from(panel.querySelectorAll('[data-size][aria-pressed="true"]'), (button) => button.dataset.size)
    );

    for (const column of panel.querySelectorAll('[data-col]')) {
      column.hidden = !selected.has(column.dataset.col);
    }

    // With nothing selected there is no table to show, only the prompt to pick
    // a size — the empty state the reference uses.
    const table = panel.querySelector('[data-table]');
    // const hint = panel.querySelector('[data-hint]');
    const hasSelection = selected.size > 0;

    if (table) table.hidden = !hasSelection;
    // if (hint) hint.hidden = hasSelection;

    const units = panel.querySelector('[data-units]');
    if (units) units.hidden = !hasSelection;

    const unit = panel.dataset.activeUnit === 'inch' ? 'inch' : 'cm';
    const exact = panel.dataset.precision === 'exact';

    for (const button of panel.querySelectorAll('[data-unit]')) {
      button.setAttribute('aria-pressed', String(button.dataset.unit === unit));
    }

    for (const cell of panel.querySelectorAll('[data-cm]')) {
      cell.textContent = formatMeasurement(cell.dataset.cm ?? '', unit, exact);
    }
  }

  /**
   * @param {Event} event
   * @param {string} selector
   */
  #closest(event, selector) {
    const target = event.target;
    return target instanceof Element ? target.closest(selector) : null;
  }

  /**
   * @param {Event} event
   * @param {string} key
   */
  #targetData(event, key) {
    const element = this.#closest(event, `[data-${key}]`);
    return element instanceof HTMLElement ? element.dataset[key] : undefined;
  }
}

/**
 * Converts a centimetre value or range into the requested unit.
 *
 * Ranges stay ranges. Inches are quoted to the nearest eighth when the figure is
 * a precise garment measurement, and to whole inches for body ranges — which is
 * how the reference prints them.
 *
 * @param {string} value - Centimetres, e.g. "78" or "76-79".
 * @param {'cm' | 'inch'} unit
 * @param {boolean} exact - Quote inches to the nearest eighth.
 * @returns {string}
 */
export function formatMeasurement(value, unit, exact) {
  const raw = value.trim();
  if (!NUMERIC.test(raw)) return raw;

  return raw
    .split('-')
    .map((part) => {
      const cm = Number.parseFloat(part.trim().replace(',', '.'));
      if (Number.isNaN(cm)) return part.trim();
      return unit === 'inch' ? formatInches(cm / CM_PER_INCH, exact) : formatCentimetres(cm);
    })
    .join('-');
}

/**
 * @param {number} cm
 * @returns {string}
 */
function formatCentimetres(cm) {
  // Whole centimetres unless the source carries a decimal worth keeping.
  return Number.isInteger(cm) ? String(cm) : String(Math.round(cm * 10) / 10);
}

/**
 * @param {number} inches
 * @param {boolean} exact
 * @returns {string}
 */
function formatInches(inches, exact) {
  if (!exact) return String(Math.round(inches));

  let whole = Math.floor(inches);
  let eighths = Math.round((inches - whole) * 8);

  if (eighths === 8) {
    whole += 1;
    eighths = 0;
  }

  return eighths === 0 ? String(whole) : `${whole} ${EIGHTHS[eighths]}`;
}

/**
 * @typedef {{ headers: string[], rows: string[][] }} Guide
 * Each row holds the size first, then one value per header.
 */

/**
 * Downloads a sheet as rows of cells. Shared between components on the page
 * and cached in sessionStorage for a few minutes.
 * @param {string} url - The sheet's share or edit link, or a published CSV link.
 * @returns {Promise<string[][]>}
 */
export function fetchSheet(url) {
  const csvUrl = toCsvUrl(url);
  const pending = sheetRequests.get(csvUrl);
  if (pending) return pending;

  const request = (async () => {
    const key = `size-guide:${csvUrl}`;
    const cached = readCache(key);
    if (cached) return parseCsv(cached);

    const response = await fetch(csvUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const text = await response.text();
    writeCache(key, text);
    return parseCsv(text);
  })();

  // A failed request should be retried on the next page, not remembered.
  request.catch(() => sheetRequests.delete(csvUrl));
  sheetRequests.set(csvUrl, request);
  return request;
}

/**
 * Turns a Google Sheets link into its CSV export, keeping the tab (gid).
 * Links that already point at a CSV are returned untouched.
 * @param {string} url
 * @returns {string}
 */
export function toCsvUrl(url) {
  const trimmed = url.trim();
  if (/output=csv|format=csv|tqx=out:csv/.test(trimmed)) return trimmed;

  const id = trimmed.match(/\/spreadsheets\/d\/([\w-]+)/)?.[1];
  if (!id) return trimmed;

  const gid = trimmed.match(/[#?&]gid=(\d+)/)?.[1] ?? '0';
  return `https://docs.google.com/spreadsheets/d/${id}/export?format=csv&gid=${gid}`;
}

/**
 * Picks the product's rows out of the sheet.
 *
 * A sheet SKU such as "366803.57892-XS" belongs to the product when its base
 * ("366803.57892", size suffix removed) begins one of the variant SKUs or sits
 * in the handle, ignoring punctuation — the store mixes "366803.57892-XS",
 * "36680357892GG" and handles ending in "366803-57892".
 *
 * @param {string[][]} table
 * @param {{ handle: string, skus: string[], sizes: string[] }} product
 * @returns {Guide | null}
 */
export function findProductGuide(table, product) {
  const [header, ...records] = table;
  if (!header) return null;

  const skuIndex = header.findIndex((label) => SKU_COLUMN.test(label.trim()));
  const sizeIndex = header.findIndex((label) => SIZE_COLUMN.test(label.trim()));
  if (skuIndex === -1 || sizeIndex === -1) return null;

  const measureIndexes = header
    .map((label, index) => ({ label: label.trim(), index }))
    .filter(({ label, index }) => index !== skuIndex && index !== sizeIndex && label && !IGNORED_COLUMNS.test(label));

  const handle = compact(product.handle);
  const skus = product.skus.map(compact).filter(Boolean);
  const sizes = new Set(product.sizes);

  /** @type {string | null} */
  let matchedBase = null;
  /** @type {string[][]} */
  const matches = [];

  for (const record of records) {
    const sku = (record[skuIndex] ?? '').trim().toUpperCase();
    const size = normaliseSize(record[sizeIndex] ?? '');
    if (!sku || !size) continue;

    const base = compact(stripSizeSuffix(sku, (record[sizeIndex] ?? '').trim().toUpperCase()));
    if (!base) continue;

    // Only one product per page: the first base that matches wins.
    if (matchedBase === null) {
      const belongs = (handle && handle.includes(base)) || skus.some((variant) => variant.startsWith(base));
      if (!belongs) continue;
      matchedBase = base;
    } else if (base !== matchedBase) {
      continue;
    }

    // Sizes the product doesn't sell are left out. Without a size option, keep all.
    if (sizes.size > 0 && !sizes.has(size)) continue;
    if (matches.some((row) => row[0] === size)) continue;

    matches.push([size, ...measureIndexes.map(({ index }) => (record[index] ?? '').trim())]);
  }

  if (matches.length === 0) return null;

  // Columns this product has no figures for are dropped, e.g. Manga on a skirt.
  const used = measureIndexes.map((_, column) => matches.some((row) => row[column + 1] !== ''));
  if (!used.includes(true)) return null;

  return {
    headers: measureIndexes.filter((_, column) => used[column]).map(({ label }) => cleanHeader(label)),
    rows: matches.map(([size, ...values]) => [size ?? '', ...values.filter((_, column) => used[column])]),
  };
}

/**
 * Writes the size toggles, the size columns and one row per measurement into
 * an empty panel. Everything starts hidden; #render reveals the selection.
 * @param {Element} panel
 * @param {Guide} guide
 */
function fillPanel(panel, guide) {
  const sizes = panel.querySelector('[data-sizes]');
  const head = panel.querySelector('[data-head]');
  const body = panel.querySelector('[data-body]');

  for (const [size = ''] of guide.rows) {
    if (sizes) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'size-guide__size';
      button.dataset.size = size;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('on:click', '/toggleSize');
      button.textContent = size;
      sizes.append(button);
    }

    if (head) {
      const th = document.createElement('th');
      th.scope = 'col';
      th.dataset.col = size;
      th.hidden = true;
      th.textContent = size;
      head.append(th);
    }
  }

  if (!body) return;

  guide.headers.forEach((label, column) => {
    const row = document.createElement('tr');
    const th = document.createElement('th');
    th.scope = 'row';
    th.textContent = label;
    row.append(th);

    for (const [size = '', ...values] of guide.rows) {
      const td = document.createElement('td');
      const value = values[column] ?? '';
      td.dataset.col = size;
      td.dataset.cm = value;
      td.hidden = true;
      td.textContent = value;
      row.append(td);
    }

    body.append(row);
  });
}

/**
 * Minimal RFC 4180 parser: quoted fields, escaped quotes, commas and line
 * breaks inside quotes.
 * @param {string} text
 * @returns {string[][]}
 */
export function parseCsv(text) {
  /** @type {string[][]} */
  const rows = [];
  /** @type {string[]} */
  let row = [];
  let field = '';
  let quoted = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (quoted) {
      if (char === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        quoted = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''));
}

/**
 * @param {string} value
 * @returns {string}
 */
function normaliseSize(value) {
  const size = value.trim().toUpperCase();
  return SIZE_ALIASES[/** @type {keyof typeof SIZE_ALIASES} */ (size)] ?? size;
}

/**
 * "366803.57892-XS" → "366803.57892". SKUs without the size are left as they are.
 * @param {string} sku
 * @param {string} size
 */
function stripSizeSuffix(sku, size) {
  if (!size || !sku.endsWith(size)) return sku;
  return sku.slice(0, -size.length).replace(/[-_./\s]+$/, '');
}

/**
 * Uppercase letters and digits only, so SKUs compare regardless of punctuation.
 * @param {string} value
 */
function compact(value) {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * "Busto/Pecho (cm)" → "Busto/Pecho". The unit is shown by the switch instead.
 * @param {string} label
 */
function cleanHeader(label) {
  return label.replace(/\s*\((cm|in|inch|pulgadas)\)\s*$/i, '').trim();
}

/**
 * @param {string | undefined} value - Items separated by `|`
 * @returns {string[]}
 */
function splitList(value) {
  return (value ?? '')
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);
}

/** @param {string} key */
function readCache(key) {
  try {
    const entry = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    return entry && Date.now() - entry.time < SHEET_TTL_MS ? entry.text : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} text
 */
function writeCache(key, text) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), text }));
  } catch {
    // Storage full or blocked: the sheet is simply fetched again next time.
  }
}

if (!customElements.get('size-guide-component')) {
  customElements.define('size-guide-component', SizeGuideComponent);
}
